/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Cycle product images during touch gestures without blocking scrolling or links.
(() => {
  let touchedMedia = null;
  let cycleTimer = null;
  const positions = new WeakMap();
  const previews = new WeakMap();
  const sourcesFor = card => card.dataset.sgmImages
    ? JSON.parse(card.dataset.sgmImages)
    : (card.product?.images || []).map(image => typeof image === 'string' ? image : image.src);
  const prepare = (card, media, position) => {
    let images = previews.get(card);
    if (!images) previews.set(card, images = new Map());
    if (images.has(position)) return images.get(position);
    const image = media.querySelector('img.product-card__img-hover').cloneNode(false);
    image.classList.add('sgm-touch-image');
    image.removeAttribute('srcset');
    image.removeAttribute('product-card-secondary-image');
    image.loading = 'eager';
    image.src = sourcesFor(card)[position];
    images.set(position, image);
    return image;
  };

  // Warm only the next image for cards near the viewport on touch devices.
  if (window.matchMedia('(any-pointer: coarse)').matches) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(({ target: card, isIntersecting }) => {
        if (!isIntersecting) return;
        const media = card.querySelector('.product-card__media--hover-image');
        if (media?.querySelector('img.product-card__img-hover') && sourcesFor(card).length > 1) {
          sourcesFor(card).forEach((source, index) => prepare(card, media, index));
          observer.unobserve(card);
        }
      });
    }, { rootMargin: '300px' });
    const watch = root => {
      if (root.matches?.('product-card')) observer.observe(root);
      root.querySelectorAll?.('product-card').forEach(card => observer.observe(card));
    };
    watch(document);
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(watch)))
      .observe(document.body, { childList: true, subtree: true });
  }
  const clearPreview = () => {
    clearInterval(cycleTimer);
    cycleTimer = null;
    touchedMedia?.classList.remove('sgm-touch-preview');
    touchedMedia?.querySelector('.sgm-touch-image')?.remove();
    touchedMedia = null;
  };

  document.addEventListener('touchstart', (event) => {
    clearPreview();
    const top = event.target.closest?.('.product-card-top, .product-card__top');
    const card = event.target.closest?.('product-card');
    const media = top?.querySelector('.product-card__media--hover-image')
      || event.target.closest?.('.product-card__media--hover-image');
    if (!media || !media.querySelector('img.product-card__img-hover.hover-enabled')) return;
    if (!card || event.target.closest?.('button')) return;
    const sources = sourcesFor(card);
    if (sources.length < 2) return;
    touchedMedia = media;
    const advance = () => {
      const position = ((positions.get(card) ?? 0) + 1) % sources.length;
      const preview = prepare(card, media, position);
      const show = () => {
        if (touchedMedia !== media) return;
        media.querySelector('.sgm-touch-image')?.remove();
        media.append(preview);
        media.classList.add('sgm-touch-preview');
        positions.set(card, position);
      };
      preview.onload = show;
      if (preview.complete && preview.naturalWidth > 0) show();
      prepare(card, media, (position + 1) % sources.length);
    };
    advance();
    cycleTimer = setInterval(advance, 550);
  }, { passive: true, capture: true });

  const stopCycling = () => {
    clearInterval(cycleTimer);
    cycleTimer = null;
  };
  document.addEventListener('touchend', stopCycling, { passive: true, capture: true });
  document.addEventListener('touchcancel', stopCycling, { passive: true, capture: true });
  window.addEventListener('blur', clearPreview);

  // Desktop starts with the native second-image hover, then cycles the gallery.
  let desktopMedia = null;
  let desktopTimer = null;
  let desktopGeneration = 0;
  const clearDesktop = () => {
    desktopGeneration++;
    clearInterval(desktopTimer);
    desktopTimer = null;
    desktopMedia?.classList.remove('sgm-touch-preview');
    desktopMedia?.querySelector('.sgm-touch-image')?.remove();
    desktopMedia = null;
  };
  document.addEventListener('pointerover', event => {
    if (event.pointerType !== 'mouse') return;
    const media = event.target.closest?.('.product-card__media--hover-image');
    if (!media || media === desktopMedia) return;
    clearDesktop();
    const card = media.closest('product-card');
    if (!card || !media.querySelector('img.product-card__img-hover.hover-enabled')) return;
    const sources = sourcesFor(card);
    if (sources.length < 3) return;
    desktopMedia = media;
    const generation = desktopGeneration;
    let position = 1;
    sources.forEach((source, index) => prepare(card, media, index));
    desktopTimer = setInterval(() => {
      const next = (position + 1) % sources.length;
      const image = prepare(card, media, next);
      const show = () => {
        if (generation !== desktopGeneration || desktopMedia !== media) return;
        media.querySelector('.sgm-touch-image')?.remove();
        media.append(image);
        media.classList.add('sgm-touch-preview');
        position = next;
      };
      image.onload = show;
      if (image.complete && image.naturalWidth > 0) show();
    }, 800);
  }, { passive: true });
  document.addEventListener('pointerout', event => {
    if (event.pointerType === 'mouse' && desktopMedia
      && !desktopMedia.contains(event.relatedTarget)) clearDesktop();
  }, { passive: true });
  window.addEventListener('blur', clearDesktop);
})();
